// components/SavedCardSection/SavedCardSection.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSavedCard, deleteSavedCard } from "../../Redux/Actions/savedCardActions";
import { toast } from "react-toastify";
import "./Savedcardsection.css";

// Maps Square card brand strings to Font Awesome brand icon classes
const BRAND_ICONS = {
  VISA:       "fa-cc-visa",
  MASTERCARD: "fa-cc-mastercard",
  AMEX:       "fa-cc-amex",
  DISCOVER:   "fa-cc-discover",
  JCB:        "fa-cc-jcb",
  DINERS:     "fa-cc-diners-club",
};

const toastOpts = {
  pauseOnFocusLoss: false,
  draggable: false,
  pauseOnHover: false,
  autoClose: 2500,
};

const SavedCardSection = () => {
  const dispatch  = useDispatch();
  const toastId   = React.useRef(null);

  const { savedCard, loading, error } = useSelector((state) => state.savedCard);
  const { userInfo }                  = useSelector((state) => state.userLogin);

  const [removing, setRemoving]     = useState(false);
  const [confirmed, setConfirmed]   = useState(false); // show inline confirm

  useEffect(() => {
    if (userInfo?.token) {
      dispatch(fetchSavedCard());
    }
  }, [dispatch, userInfo]);

  const handleRemoveClick = () => {
    setConfirmed(true); // show inline confirm instead of window.confirm
  };

  const handleConfirmRemove = async () => {
    setRemoving(true);
    setConfirmed(false);
    await dispatch(deleteSavedCard());
    setRemoving(false);
    if (!toast.isActive(toastId.current)) {
      toastId.current = toast.success("Card removed successfully", toastOpts);
    }
  };

  const handleCancelRemove = () => {
    setConfirmed(false);
  };

  const brandIcon = savedCard
    ? BRAND_ICONS[savedCard.cardBrand] || "fa-credit-card fas"
    : null;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="row mb-4">
      <div className="col-12">
        <div className="saved-card-profile-card">

          {/* ── Header ──────────────────────────────────────────────────── */}
          <div className="scp-header">
            <div className="scp-header-left">
              <div className="scp-icon-wrapper">
                <i className="fas fa-credit-card"></i>
              </div>
              <div>
                <h5 className="mb-0">Payment Method</h5>
                <p className="scp-subtitle">
                  {savedCard
                    ? "Your card is saved for faster checkout"
                    : "Save a card for one-click checkout"}
                </p>
              </div>
            </div>

            {/* Status badge */}
            <div className={`scp-badge ${savedCard ? "scp-badge--saved" : "scp-badge--none"}`}>
              <i className={`fas ${savedCard ? "fa-shield-alt" : "fa-times-circle"}`}></i>
              <span>{savedCard ? "Card Saved" : "No Card"}</span>
            </div>
          </div>

          {/* ── Body ────────────────────────────────────────────────────── */}
          <div className="scp-body">
            {loading ? (
              <div className="scp-loading">
                <i className="fas fa-spinner fa-spin"></i>
                <span>Loading payment info…</span>
              </div>
            ) : savedCard ? (
              /* ── Card on file display ─────────────────────────────── */
              <div className="scp-card-display">
                <div className="scp-card-visual">
                  <div className="scp-card-chip"></div>
                  <div className="scp-card-number">
                    <span>••••</span>
                    <span>••••</span>
                    <span>••••</span>
                    <span>{savedCard.last4}</span>
                  </div>
                  <div className="scp-card-footer">
                    <span className="scp-card-brand">{savedCard.cardBrand}</span>
                    <i className={`fab ${brandIcon} scp-card-brand-icon`}></i>
                  </div>
                </div>

                <div className="scp-card-actions">
                  <div className="scp-card-meta">
                    <i className={`fab ${brandIcon} scp-meta-brand-icon`}></i>
                    <div className="scp-meta-text">
                      <span className="scp-meta-number">
                        {savedCard.cardBrand} ending in {savedCard.last4}
                      </span>
                      <span className="scp-meta-note">
                        <i className="fas fa-check-circle"></i>
                        Used automatically at checkout
                      </span>
                    </div>
                  </div>

                  {/* Inline remove confirmation */}
                  {confirmed ? (
                    <div className="scp-confirm-remove">
                      <span>Remove this card?</span>
                      <button
                        className="scp-btn-confirm-yes"
                        onClick={handleConfirmRemove}
                        disabled={removing}
                      >
                        {removing ? <i className="fas fa-spinner fa-spin"></i> : "Yes, remove"}
                      </button>
                      <button
                        className="scp-btn-confirm-no"
                        onClick={handleCancelRemove}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      className="scp-btn-remove"
                      onClick={handleRemoveClick}
                      disabled={removing}
                    >
                      <i className="fas fa-trash-alt"></i>
                      <span>Remove Card</span>
                    </button>
                  )}
                </div>
              </div>
            ) : (
              /* ── No card — info state ─────────────────────────────── */
              <div className="scp-empty">
                <div className="scp-empty-icon">
                  <i className="fas fa-credit-card"></i>
                </div>
                <div className="scp-empty-text">
                  <h6>No Payment Method Saved</h6>
                  <p>
                    You can save a card during checkout by checking
                    <strong> "Save card for future purchases"</strong> on the payment page.
                    It will then appear here and be used automatically next time.
                  </p>
                </div>
                <div className="scp-perks">
                  <div className="scp-perk">
                    <i className="fas fa-bolt"></i>
                    <span>Faster checkout</span>
                  </div>
                  <div className="scp-perk">
                    <i className="fas fa-lock"></i>
                    <span>Secured by Square</span>
                  </div>
                  <div className="scp-perk">
                    <i className="fas fa-undo"></i>
                    <span>Remove anytime</span>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <p className="scp-error">
                <i className="fas fa-exclamation-circle"></i> {error}
              </p>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default SavedCardSection;